/**
 * German B2B greeting / sign-off matrix — client-side mirror of
 * `apps/backend/src/libs/correspondence/salutation.ts`. Kept in sync by hand
 * because the monorepo has no shared package yet. When changing the matrix,
 * update **both** files and run the backend `salutation.spec.ts` to confirm
 * the rules still match.
 *
 * Reasoning duplicated here so the message-composer card can render the
 * resolved greeting client-side without an extra server roundtrip — the AI
 * emits `{{anrede}}` / `{{grussformel}}` tokens and we resolve them against
 * the already-looked-up contact + the logged-in user.
 */

export type SalutationCode = 'D' | 'S' | null;
export type GenderCode = string | null;

export interface AnredeContext {
  firstName: string | null;
  lastName: string | null;
  salutation: SalutationCode;
  gender: GenderCode;
}

export interface GrussformelContext {
  duSie: SalutationCode;
  sender: { firstName: string; lastName: string } | null;
}

function clean(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

const isDuzen = (code: SalutationCode) => code === 'D';
const isMale = (gender: GenderCode) => gender === 'gt_Male';
const isFemale = (gender: GenderCode) => gender === 'gt_Female';

export function computeAnrede(contact: AnredeContext | null): string {
  if (!contact) return 'Sehr geehrte Damen und Herren';

  const firstName = clean(contact.firstName);
  const lastName = clean(contact.lastName);

  if (isDuzen(contact.salutation)) {
    return firstName ? `Hallo ${firstName}` : 'Hallo zusammen';
  }
  if (isMale(contact.gender) && lastName) {
    return `Sehr geehrter Herr ${lastName}`;
  }
  if (isFemale(contact.gender) && lastName) {
    return `Sehr geehrte Frau ${lastName}`;
  }
  return 'Sehr geehrte Damen und Herren';
}

export function computeGrussformel(ctx: GrussformelContext): string {
  const du = isDuzen(ctx.duSie);
  const closing = du ? 'Viele Grüße' : 'Mit freundlichen Grüßen';
  if (!ctx.sender) {
    return `${closing}\nIhr Team der eLink Distribution AG`;
  }
  const firstName = clean(ctx.sender.firstName) ?? '';
  const lastName = clean(ctx.sender.lastName) ?? '';
  const signer = du
    ? firstName || lastName || 'Ihr Team der eLink Distribution AG'
    : [firstName, lastName].filter(Boolean).join(' ') ||
      'Ihr Team der eLink Distribution AG';
  return `${closing}\n${signer}`;
}
